import React, { useCallback, useEffect, useState } from "react";
import { FaCog } from "react-icons/fa";
import { Navigate, useParams } from "react-router-dom";
import PartyAlertsBanner from "./PartyAlertsBanner";
import { CompensationTable, DetailChip, DetailField } from "./DetailsLayout";
import { translateMatterStatus } from "../helpers/clientDisplayHelper";
import { getCaseTypeLabel } from "../helpers/globalSearchHelper";
import Breadcrumb from "./Breadcrumb";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../context/TranslationContext";
import {
  fetchCoordinatorClientDetails,
  fetchCoordinatorSalesforceFiles,
  fetchDownloadUrl,
  fetchFilesPreferGrouped,
} from "../helpers/apiHelper";
import CategorizedDocuments, { buildMissingDocumentsAlert } from "./CategorizedDocuments";
import { useDocumentPreview } from "./DocumentViewer";
import useCategorizedDocuments from "../hooks/useCategorizedDocuments";
import { useWhatsAppMatter } from "../context/WhatsAppContactContext";
import { isMockDocumentId } from "../helpers/documentCategories";
import "../styles/Documents.css";
import { formatDateToYYYYMMDD } from "../helpers/utilityHelper";
import "../styles/ClientDetails.css";
import "../styles/Views.css";
import "../styles/PartyDetails.css";

const valueOrDash = (value) => value || "—";

const CoordinatorClientDetails = () => {
  const { cocoId, matterId } = useParams();
  const { user, setUser, tokens } = useAuth();
  const { t } = useTranslation();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingFileId, setDownloadingFileId] = useState(null);
  const [downloadError, setDownloadError] = useState(null);
  const [error, setError] = useState("");
  const listPreview = useDocumentPreview({
    fetchViewUrl: (doc) => fetchDownloadUrl(doc.id, tokens.idToken),
    onDownload: (doc) => handleDownload(doc.id),
    onError: (key) => setDownloadError(t(key)),
  });

  const coordinatorAccessDenied = user?.capabilities?.canAccessCoordinator === false;

  const loadClient = useCallback(async () => {
    if (coordinatorAccessDenied || !tokens?.idToken || !cocoId || !matterId) return;

    setLoading(true);
    setError("");
    try {
      const result = await fetchCoordinatorClientDetails(
        cocoId,
        matterId,
        tokens.idToken,
      );
      setClient(result);
      setUser((currentUser) => ({
        ...(currentUser || {}),
        capabilities: {
          ...(currentUser?.capabilities || {}),
          canAccessCoordinator: true,
        },
      }));
    } catch (requestError) {
      console.error("Failed to fetch coordinator client details:", requestError);
      setError(t("coordinatorClientDetails.loadError"));
    } finally {
      setLoading(false);
    }
  }, [cocoId, coordinatorAccessDenied, matterId, setUser, tokens?.idToken, t]);

  const loadFiles = useCallback(
    () => fetchFilesPreferGrouped(fetchCoordinatorSalesforceFiles, cocoId, matterId, tokens?.idToken),
    [cocoId, matterId, tokens?.idToken],
  );
  useWhatsAppMatter(client?.details?.matterName);
  // The dev mock shows the coordinator read-only view (another co-counsel's client).
  const docs = useCategorizedDocuments({
    load: loadFiles,
    matterId,
    token: tokens?.idToken,
    author: user?.email,
    mockCanEdit: false,
  });
  const { reload: reloadDocuments } = docs;
  const documents = docs.files;
  const loadingDocuments = docs.loading;
  const documentsError = docs.failed ? t("clientDetails.documentLoadError") : "";

  const loadDocuments = useCallback(() => {
    if (coordinatorAccessDenied || !tokens?.idToken || !matterId) return;
    reloadDocuments();
  }, [coordinatorAccessDenied, matterId, reloadDocuments, tokens?.idToken]);

  useEffect(() => {
    loadClient();
    loadDocuments();
  }, [loadClient, loadDocuments]);

  if (coordinatorAccessDenied) {
    return <Navigate to="/" replace />;
  }

  const handleDownload = async (fileId) => {
    setDownloadError(null);
    if (isMockDocumentId(fileId)) {
      setDownloadError(t("documents.errors.notAvailable"));
      return;
    }
    try {
      setDownloadingFileId(fileId);
      const signedUrl = await fetchDownloadUrl(fileId, tokens.idToken);
      window.open(signedUrl, "_blank", "noopener,noreferrer");
    } catch (downloadError) {
      console.error("Failed to download document:", downloadError);
      setDownloadError(t("documents.errors.download"));
    } finally {
      setDownloadingFileId(null);
    }
  };

  const details = client?.details || {};
  const compensationRecords = client?.compensation?.records || [];
  const isBhp = (details.caseType || "").toUpperCase().includes("BHP");
  const hasCompensationSection =
    isBhp || Boolean(client?.compensation?.hasCompensation) || compensationRecords.length > 0;

  return (
    <div className="view-container coordinator-client-details party-details">
      <div className="portal-page-breadcrumb">
        <Breadcrumb
          items={[
            {
              label: t("breadcrumb.coordinatorDashboard"),
              link: "/",
              state: { activePortal: "coordinator" },
            },
            {
              label: t("breadcrumb.coordinatorDetails"),
              link: `/coordinator/${encodeURIComponent(cocoId || "")}`,
            },
            { label: t("breadcrumb.coordinatorClientDetails") },
          ]}
        />
      </div>

      {loading ? (
        <header className="portal-page-panel party-header-card">
          <p className="portal-page-kicker">{t("breadcrumb.coordinatorClientDetails")}</p>
          <p className="party-loading" role="status">
            <FaCog className="spinner-cog" aria-hidden="true" />
            {t("loading")}
          </p>
        </header>
      ) : error ? (
        <header className="portal-page-panel party-header-card">
          <p className="portal-page-kicker">{t("breadcrumb.coordinatorClientDetails")}</p>
          <div className="party-load-error" role="alert">
            <p>{error}</p>
            <button type="button" className="portal-button" onClick={loadClient}>
              {t("coordinatorDashboard.retry")}
            </button>
          </div>
        </header>
      ) : (
        <>
          <PartyAlertsBanner
            alerts={client?.alerts || []}
            extraItems={buildMissingDocumentsAlert(docs.missing, t, docs.focusCategory)}
          />

          <section className="portal-page-panel party-main-card" aria-labelledby="coordinator-client-name">
            <header className="party-main-header">
              <p className="portal-page-kicker">
                {t("portal.coordinator")} · {t("breadcrumb.coordinatorClientDetails")}
              </p>
              <h2 className="party-name" id="coordinator-client-name" data-testid="coordinator-client-name">
                {details.partyName || t("coordinatorClientDetails.title")}
              </h2>
              {(details.matterName || details.bhpMasterSchedule) && (
                <ul className="party-chips">
                  {details.matterName && (
                    <DetailChip label={t("clientsTable.matterName")} testId="coordinator-chip-matter">
                      {details.matterName}
                    </DetailChip>
                  )}
                  {details.bhpMasterSchedule && (
                    <DetailChip label={t("clientsTable.masterSchedule")} testId="coordinator-chip-master-schedule">
                      {details.bhpMasterSchedule}
                    </DetailChip>
                  )}
                </ul>
              )}
            </header>

            <div className="party-columns">
              <section className="party-column" aria-labelledby="coordinator-client-info-title">
                <div className="party-card-header">
                  <h3 id="coordinator-client-info-title">{t("coordinatorClientDetails.clientInformation")}</h3>
                </div>
                <div className="party-field-grid is-two-columns">
                  <Detail label={t("clientsTable.cpf")} value={details.cpf} />
                  <Detail label={t("clientsTable.case")} value={getCaseTypeLabel(details.caseType, t) || details.caseType} />
                  <Detail
                    label={t("clientsTable.matterStatus")}
                    value={translateMatterStatus(details.matterStatus, t)}
                  />
                  <Detail
                    label={t("clientsTable.retainerSignedDate")}
                    value={formatDateToYYYYMMDD(details.retainerSignedDate)
                      || t("clientsTable.emptyStates.notYetSigned")}
                  />
                  <Detail
                    label={t("clientsTable.email")}
                    value={details.email || t("clientsTable.emptyStates.noEmailAddress")}
                  />
                  <Detail
                    label={t("clientsTable.phone")}
                    value={details.phone || t("clientsTable.emptyStates.noPhoneNumber")}
                  />
                </div>
              </section>

              <section className="party-column" aria-labelledby="coordinator-questionnaire-title">
                <div className="party-card-header">
                  <h3 id="coordinator-questionnaire-title">{t("coordinatorClientDetails.questionnaire.title")}</h3>
                </div>
                <div className="party-field-grid is-two-columns">
                  <Detail
                    label={t("coordinatorClientDetails.questionnaire.available")}
                    value={client?.questionnaire?.available ? t("coordinatorDetails.yes") : t("coordinatorDetails.no")}
                  />
                  <Detail
                    label={t("clientsTable.questionnaireCompletedDate")}
                    value={formatDateToYYYYMMDD(client?.questionnaire?.completedAt)
                      || t("clientsTable.emptyStates.notYetCompleted")}
                  />
                  {client?.questionnaire?.link && (
                    <DetailField label={t("clientsTable.questionnaireLink")}>
                      <a className="party-inline-link" href={client.questionnaire.link} target="_blank" rel="noreferrer">
                        {t("coordinatorClientDetails.questionnaire.open")}
                      </a>
                    </DetailField>
                  )}
                </div>
                {isBhp && !client?.discontinuance?.available && (
                  <p className="detail-empty-state party-column-note">
                    {t("coordinatorClientDetails.questionnaire.discontinuanceUnavailable")}
                  </p>
                )}
              </section>
            </div>
          </section>

          {hasCompensationSection && (
            <section className="portal-page-panel party-card" aria-labelledby="coordinator-compensation-title">
              <h3 id="coordinator-compensation-title">{t("coordinatorClientDetails.compensation.title")}</h3>
              {compensationRecords.length > 0 ? (
                <CompensationTable records={compensationRecords} t={t} testId="coordinator-compensation-table" />
              ) : (
                <p className="detail-empty-state">
                  {t("coordinatorClientDetails.compensation.empty")}
                </p>
              )}
            </section>
          )}

          <section className="portal-page-panel party-card party-documents-card" aria-labelledby="coordinator-documents-title">
            <h3 id="coordinator-documents-title">{t("clientDetails.documentsTitle")}</h3>
            {downloadError && (
              <div className="status-message is-error" role="alert" data-testid="docs-download-error">{downloadError}</div>
            )}
            {loadingDocuments ? (
              <p className="mb-10 flex-row">
                <FaCog className="spinner-cog" />
                {t("loading")}
              </p>
            ) : documentsError ? (
              <p className="detail-empty-state">{documentsError}</p>
            ) : docs.grouped ? (
              <CategorizedDocuments
                data={docs.grouped}
                canEdit={docs.grouped.canEdit}
                uploadsDisabled={Boolean(client?.termination)}
                onView={(doc) => handleDownload(doc.id)}
                viewingId={downloadingFileId}
                fetchViewUrl={(doc) => fetchDownloadUrl(doc.id, tokens.idToken)}
                onUpload={docs.upload}
                onReclassify={docs.reclassify}
                focusRequest={docs.focusRequest}
                idPrefix="coordinator-docs"
              />
            ) : documents.length > 0 ? (
              <div className="client-documents">
                {documents.map((document) => (
                  <div className="flex-row" key={document.id}>
                    <button
                      type="button"
                      className="link"
                      onClick={() => {
                        setDownloadError(null);
                        listPreview.open(document);
                      }}
                      disabled={downloadingFileId === document.id || listPreview.previewingId === document.id}
                    >
                      {document.name}
                    </button>
                    {(downloadingFileId === document.id || listPreview.previewingId === document.id) && (
                      <FaCog className="spinner-cog ml-2" />
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="detail-empty-state">{t("clientDetails.noDocumentsFound")}</p>
            )}
            {listPreview.viewerElement}
          </section>
        </>
      )}
    </div>
  );
};

const Detail = ({ label, value }) => <DetailField label={label} value={valueOrDash(value)} />;

export default CoordinatorClientDetails;
